/**
 * Creates a debounced function that delays invoking the provided callback function.
 * The debounced function returns a Promise that resolves with the result of the callback function.
 *
 * @template T - The type of the arguments to the callback function.
 * @template U - The type of the result of the callback function.
 * @param {(...args: T) => PromiseLike<U> | U} callback - The function to debounce.
 * @param {number} wait - The number of milliseconds to delay.
 * @returns {(...args: T) => Promise<U>} - A new function that can be called with the same arguments as the callback function.
 *
 * When the new function is invoked, it delays invoking the callback function until after `wait` milliseconds have elapsed since the last time the new function was invoked.
 *
 * @example
 * const debounced = debounce(async (x: number) => x * 2, 1000);
 * debounced(1).then(result => console.log(result)); // 2
 */
export function debounce<T extends unknown[], U>(callback: (...args: T) => PromiseLike<U> | U, wait = DEFAULT_DEBOUNCE_DURATION): (...args: T) => Promise<U> {
	let timer: ReturnType<typeof setTimeout>;

	return (...args: T): Promise<U> => {
		clearTimeout(timer);
		return new Promise(resolve => {
			timer = setTimeout(() => resolve(callback(...args)), wait);
		});
	};
}

export const DEFAULT_DEBOUNCE_DURATION = 250;
